// lib/site-data.js — plain data (no 'use client'), safe to import from both
// server components (route files generating metadata/JSON-LD/sitemap) and
// client components. Values that started life inside a 'use client' module
// (components/home.jsx, components/pages.jsx) can't be `.map()`-ed from a
// server file — Next treats every export of a client module as an opaque
// client reference — so the shared data lives here instead, and those
// components import it back.

// Contact and Careers still have fully working, PHP-backed forms — this
// just switches both to a plain "email us" fallback for now (client
// request: hide, don't remove, they'll reactivate later). Flip to true to
// bring the real forms back; no other code changes needed.
export const FORMS_ENABLED = false;

export const CAPABILITIES = [
  {
    id: 'ideation',
    label: 'Ideation',
    tag: 'Concept & R&D',
    headline: 'Turning complex problems into practical engineering.',
    blurb: 'From client challenge to validated concept. We bring deep R&D, including world-class microwave technology research from our St Gallen office, to bear on new processes, products and recovery routes.',
    services: [
      ['New product development', 'From client problem to functional prototype. FMEA, design-of-experiments, patentable concepts.'],
      ['Recycling & circular process', 'Material recovery flow-sheets. Beneficiation of secondary streams. Lab through pilot scale.'],
      ['Microwave technology',  'High-frequency heating for ore pre-treatment, drying and breakage reduction. Multiphysics simulation.'],
    ],
    tone: 'studio',
  },
  {
    id: 'design',
    label: 'Design',
    tag: 'Engineering',
    headline: 'Technically sound, best-practice engineering across disciplines.',
    blurb: 'Mechanical, MV electrical, civil, process and mining engineers working from the same project room. ANSYS FEA and CFD, Aspen, ETAP. We validate in software before steel is ordered.',
    services: [
      ['Mechanical engineering',       'Structural, rotating and materials handling. FEA, CFD, fatigue and vibration analysis.'],
      ['MV electrical engineering',    'Medium-voltage reticulation, protection coordination, MCCs, SCADA integration.'],
      ['Civil engineering',            'Earthworks, foundations, stormwater. RC and steel structures.'],
      ['Process & plant design',       'Mass and energy balances. PFDs, P&IDs, 3D plant models. Hazop-ready packages.'],
      ['Mining engineering',           'Mine planning, ventilation, hoist and material handling, geotechnical interface.'],
    ],
    tone: 'industrial',
  },
  {
    id: 'execution',
    label: 'Execution',
    tag: 'EPCM & Construction',
    headline: 'Project delivery with a single accountable team.',
    blurb: 'The engineers who design the plant stay on the project through procurement, construction and commissioning. NEC4, FIDIC and PMBOK frameworks. Cost, schedule and contract administration.',
    services: [
      ['EPCM',                          'Engineering, procurement and construction management: definition through handover.'],
      ['Project & programme management','NEC4, FIDIC and PMBOK frameworks. Cost, schedule and risk management.'],
      ['Construction supervision',      'Resident engineering, QA/QC, vendor inspection and commissioning support.'],
    ],
    tone: 'earth',
  },
  {
    id: 'monitoring',
    label: 'Monitoring',
    tag: 'Operations & Lifecycle',
    headline: 'Performance, risk and opportunity: after handover.',
    blurb: 'We instrument what we build and stay involved long after commissioning. Condition monitoring, predictive analytics and lifecycle integration feed the next design and protect the operating asset.',
    services: [
      ['Civil & structural',     'Settlement, strain and crack monitoring. Tailings dam integrity.'],
      ['Mechanical performance', 'Vibration, alignment, lubrication. Bearing and gearbox condition trending.'],
      ['Electrical & control',   'Power quality, thermography, partial-discharge surveys.'],
      ['Predictive & analytical','Failure-mode modelling, RCM frameworks, ML anomaly detection.'],
      ['Lifecycle integration',  'Asset management plans, ISO-55000 alignment, OPEX optimisation.'],
    ],
    tone: 'plant',
  },
];

export const MARKETS = [
  { id: 'mining',     label: 'Mining',              blurb: 'Hard-rock, surface and underground operations across PGMs, base metals, gold, diamond and coal.', tone: 'earth' },
  { id: 'minerals',   label: 'Minerals & Metals',   blurb: 'Concentrators, smelters, refineries and recovery plants. Brownfield expansion and de-bottlenecking.', tone: 'industrial' },
  { id: 'energy',     label: 'Energy & Power',      blurb: 'Industrial power infrastructure, MV reticulation and energy-efficiency projects.', tone: 'sky' },
  { id: 'industrial', label: 'Industrial Plant',    blurb: 'Chemicals, water treatment and bulk materials handling. Project-specific process design.', tone: 'studio' },
  { id: 'research',   label: 'Technology & R&D',    blurb: 'Microwave heating, pre-treatment, novel beneficiation. From bench to pilot.', tone: 'plant' },
];

// Each client carries a logo slug + the intrinsic pixel size of its logo
// file (all normalised to 120px tall) so the <img> can carry explicit
// width/height and reserve its box before load — no layout shift.
export const CLIENTS = [
  { name: 'Anglo American',         slug: 'anglo-american',        w: 396, h: 120 },
  { name: 'Black Chrome Mine',      slug: 'bcm',                   w: 268, h: 120 },
  { name: 'Aurex Constructors',     slug: 'aurex',                 w: 159, h: 120 },
  { name: 'General Kinematics',     slug: 'general-kinematics',    w: 376, h: 120 },
  { name: 'Iritron',                slug: 'iritron',               w: 259, h: 120 },
  { name: 'R&R Quantity Surveyors', slug: 'rr-quantity-surveyors', w: 242, h: 120 },
];

export const PROJECTS = [
  {
    id: 'p1',
    sector: 'Platinum · UG2',
    title: 'Concentrator de-bottlenecking',
    location: 'Limpopo, South Africa',
    year: '2024',
    metric: '+18% throughput',
    blurb: 'Hydrocyclone re-engineering and slurry distribution redesign. ANSYS CFD model validated against plant trial. Implemented with zero unplanned downtime.',
    discipline: ['Mechanical', 'Process', 'EPCM'],
    tone: 'industrial',
  },
  {
    id: 'p2',
    sector: 'Diamond · Kimberlite',
    title: 'Microwave pre-treatment pilot',
    location: 'Northern Cape, South Africa',
    year: '2024–2025',
    metric: '−22% mill energy',
    blurb: 'Pilot-scale 100 kW continuous microwave applicator for kimberlite pre-conditioning. Multiphysics design, fabrication oversight, performance monitoring.',
    discipline: ['R&D', 'Mechanical', 'Microwave'],
    tone: 'studio',
  },
  {
    id: 'p3',
    sector: 'Base metals · Smelter',
    title: 'MV reticulation upgrade',
    location: 'North West, South Africa',
    year: '2023',
    metric: '11 kV → 22 kV',
    blurb: 'Brownfield medium-voltage upgrade across an active smelter. Protection grading, harmonic study, phased switching plan, commissioning supervision.',
    discipline: ['MV Electrical', 'EPCM'],
    tone: 'industrial',
  },
  {
    id: 'p4',
    sector: 'PGM · Tailings',
    title: 'Re-mining flow-sheet',
    location: 'Rustenburg, South Africa',
    year: '2025',
    metric: '3.4 Mt/a feed',
    blurb: 'Front-end engineering and lab programme for a tailings re-treatment plant. Recovery model, mass balance, capex estimate to AACE Class 3.',
    discipline: ['Process', 'Ideation', 'EPCM'],
    tone: 'earth',
  },
];

// ── EPCM disciplines — full service lists ───────────────────────────────────
export const EPCM_DISCIPLINES = [
  {
    id: 'mechanical', label: 'Mechanical', tag: 'Design · FEA · Equipment', tone: 'industrial',
    blurb: 'Mechanical equipment and systems design, from equipment selection and materials engineering through FEA verification, piping layout and design reviews that keep every package buildable and compliant.',
    services: [
      'Mechanical equipment design & specification',
      'Static & rotating equipment selection',
      'Materials selection & corrosion assessment',
      'Piping design support & layout development',
      'Pressure vessel & tank design support',
      'Structural & mechanical interface definition',
      'Design for manufacture & assembly (DFMA)',
      'Finite element analysis (FEA) & structural verification',
      'Fatigue, vibration & thermal stress assessments',
      'Mechanical design reviews & compliance checks',
    ],
  },
  {
    id: 'electrical', label: 'Electrical & Control', tag: 'MV · LV · I&C', tone: 'sky',
    blurb: 'Medium-voltage reticulation, protection coordination, MCCs, drive packages and SCADA integration, for greenfield builds and live brownfield upgrades.',
    services: [
      'Medium-voltage reticulation (11 / 22 / 33 kV)',
      'Protection coordination & arc-flash studies',
      'Motor control centres & VSD packages',
      'Cable routing, racking & containment',
      'Substation & transformer design',
      'SCADA, PLC & instrumentation',
      'Power-quality & harmonic studies',
      'Earthing & lightning protection',
    ],
  },
  {
    id: 'process', label: 'Process', tag: 'Flowsheets · PFDs · P&IDs', tone: 'studio',
    blurb: 'Flowsheet development, mass and energy balances, equipment sizing and the diagrams that turn a concept into a buildable, operable plant, fully integrated with every other discipline.',
    services: [
      'Geological & geotechnical studies',
      'Process flow diagrams (PFD) development',
      'Piping & instrumentation diagrams (P&IDs)',
      'Mass & energy balances',
      'Equipment sizing & specifications',
      'Process simulation & performance modelling',
      'Control philosophy & instrumentation definition',
      'Utility & off-site system definition',
      'Hazard identification & operability studies (HAZOP)',
      'Process design criteria & bases of design',
      'Integration with mechanical, electrical & civil disciplines',
    ],
  },
  {
    id: 'civil', label: 'Civil & Structural', tag: 'Foundations · Steel · Concrete', tone: 'earth',
    blurb: 'Earthworks through to the steel and concrete that carries the plant. Heavy industrial structures designed to category-4 standards.',
    services: [
      'Earthworks, bulk & detail',
      'Reinforced-concrete foundations',
      'Heavy industrial steel structures',
      'Mill, crusher & tank foundations',
      'Conveyor gantries & support trestles',
      'Buildings, control rooms & substations',
      'Stormwater & containment',
      'Geotechnical interface design',
    ],
  },
  {
    id: 'mining', label: 'Mining', tag: 'Open pit · Underground · Planning', tone: 'plant',
    blurb: 'Open-pit and underground mine design, planning and optimisation, from concept studies through to fleet productivity and operational improvement.',
    services: [
      'Open-pit & underground mine design',
      'Mine planning, scheduling & production optimisation',
      'Drill & blast design and optimisation',
      'Geotechnical & ground-control support',
      'Haulage, materials handling & mine infrastructure layout',
      'Equipment selection & fleet-productivity assessment',
      'Mining studies (concept, pre-feasibility, feasibility)',
      'Operational improvement & cost-reduction studies',
      'Integration with processing plants & surface infrastructure',
    ],
  },
  {
    id: 'bulk', label: 'Bulk Material Handling', tag: 'Conveyors · Crushing · Equipment', tone: 'industrial',
    blurb: 'Materials handling and processing equipment from ROM tip through to product stockpile: conveying, crushing and screening, mills, pumps, tanks and the reclaim and dust systems around them.',
    services: [
      'Conveyor design (CEMA / DIN 22101)',
      'Transfer chute engineering (DEM analysis)',
      'Crushing: primary, secondary, tertiary',
      'Screening & classification',
      'Cyclone clusters & classification',
      'Mill packages (SAG, ball, regrind)',
      'Feeders, hoppers & surge bins',
      'Static equipment: tanks, vessels, hoppers',
      'Slurry & process pump systems',
      'Thickeners & clarifiers',
      'Leach & CIL / CIP adsorption circuits',
      'Elution & electrowinning circuits',
      'Stockpile geometry & reclaim systems',
      'Stackers & boom conveyors',
      'Piping, valves, FEA & CFD',
      'Dust management & enclosure',
    ],
  },
];

// ── FAQ ──────────────────────────────────────────────────────────────────────
export const FAQS = [
  {
    q: 'What does Enginuity actually do?',
    a: 'We\'re a multi-disciplinary engineering, EPCM (engineering, procurement and construction management) and technology-development partner for the mining, minerals and industrial sectors: mechanical, electrical, process, civil, mining and bulk-materials-handling engineering, plus a dedicated R&D programme in high-power microwave technology and related fields.',
  },
  {
    q: 'What\'s the difference between your EPCM work and your R&D work?',
    a: 'EPCM is project delivery: engineering, procurement and construction management for mining and industrial clients, from concept through commissioning. R&D is our own research programme, led from St Gallen, developing technology (like high-power microwave systems) that often later feeds into the projects we deliver. See the Services page for both in detail.',
  },
  {
    q: 'Do you only work in South Africa?',
    a: 'Our EPCM delivery is centred on South Africa and the broader African market, run from our Centurion headquarters. Our R&D programme operates from a second office in St Gallen, Switzerland.',
  },
  {
    q: 'How do I get a quote or start a project conversation?',
    a: 'Use the contact form, or email us directly (details are on the Contact page). Send a brief, even a rough one, and we\'ll come back with how we\'d approach it, who would lead it, and what a first phase might look like. Expect a response from an engineer, not a sales rep, within two business days.',
  },
  {
    q: 'Do you take on brownfield (existing plant) work?',
    a: 'Yes, a significant share of our EPCM work is brownfield. We use LiDAR reality capture to build an accurate 3D model of the as-built plant before design starts, rather than relying on drawings that may no longer match what\'s actually on site.',
  },
  {
    q: 'Are you hiring?',
    a: 'Check the Careers page for current openings. We also accept open applications outside of listed roles: the application form lets you attach a CV either way.',
  },
  {
    q: 'How can I stay updated on your work?',
    a: 'The Insights section covers engineering perspectives, project case studies and company news as we publish them.',
  },
];

// ── Careers ──────────────────────────────────────────────────────────────────
export const OPEN_ROLES = [
  {
    id: 'civil',
    discipline: 'Design · Civil & Structural',
    title: 'Civil / Structural Engineer (Intermediate)',
    meta: ['Centurion', 'Office-based', 'Full-time'],
    blurb: 'Deliver structural and civil designs for mining and industrial projects, from analysis and detailing through to construction support. ECSA / SACPCMP registration advantageous.',
  },
  {
    id: 'mech',
    discipline: 'Design · Mechanical',
    title: 'Mechanical Engineer (Intermediate)',
    meta: ['Centurion', 'Office-based', 'Full-time'],
    blurb: 'Design mechanical systems and equipment across mining and process plants, supporting projects from concept through to commissioning. Exposure to Ansys simulation a plus.',
  },
  {
    id: 'draught',
    discipline: 'Design · Drawing Office',
    title: 'Draughtsman',
    meta: ['Centurion', 'Office-based', 'Full-time'],
    blurb: 'Produce detailed 2D and 3D drawings across disciplines using AutoCAD, Inventor and Revit, working closely with our multi-disciplinary engineering teams.',
  },
];

// datePosted/validThrough are placeholders (today, +90 days) since the
// original content has no real posting dates. Google requires both fields
// on JobPosting — keep these accurate: update datePosted whenever a role's
// copy changes, and set validThrough to the real application deadline if
// there is one, otherwise leave the 90-day rolling default.
const _today = new Date();
const _plus90 = new Date(_today.getTime() + 90 * 24 * 60 * 60 * 1000);
export const OPEN_ROLES_META = {
  datePosted: _today.toISOString().slice(0, 10),
  validThrough: _plus90.toISOString().slice(0, 10),
};

// ── R&D programmes ──────────────────────────────────────────────────────────
export const RD_AREAS = [
  {
    id: 'microwave', label: 'High-power microwave technology', tag: '915 MHz · 100–800 kW', tone: 'studio', img: '/assets/rd-microwave.webp',
    summary: 'Over more than eight years we have designed, built and field-deployed some of the highest-power industrial microwave systems in operation, multi-hundred-kilowatt 915 MHz plants that heat mineral-rich rock volumetrically to induce thermal cracking ahead of mechanical excavation.',
    detail: 'The same physics, hardware and safe-operation discipline now anchor our space and recycling research: volumetric dielectric heating, articulated waveguide applicators, Faraday shielding and closed-loop thermal control.',
    points: [
      ['100 kW → 800 kW', 'Installed microwave power, modular solid-state to in-situ rigs'],
      ['≈ 280–300 mm', 'Effective in-situ treatment depth on hard rock'],
      ['> 100% gain', 'Penetration / cutting-rate improvement from volumetric heating'],
      ['HFSS + MATLAB', 'Field-distribution modelling validated against field results'],
    ],
  },
  {
    id: 'lunar', label: 'Lunar surface construction', tag: 'In-situ ISRU · Patent pending', tone: 'industrial', img: '/assets/rd-lunar.webp',
    summary: 'A microwave method for converting loose lunar regolith into solid, load-bearing surfaces directly on the Moon, using only in-situ material, with no binders, water or aggregate shipped from Earth.',
    detail: 'Regolith’s iron- and titanium-bearing minerals couple strongly to microwave fields, so energy is deposited throughout the volume and the material sinters and melts into a coherent solid without contact, tolerant of hard vacuum and abrasive, charged dust. Our first trial achieved coalescence to 280 mm in a single three-minute 915 MHz exposure.',
    points: [
      ['Landing & ascent pads', 'Thick load-bearing slabs that suppress descent-plume ejecta'],
      ['Roads & rover traverses', 'Stable, dust-controlled routes between sites'],
      ['Foundation pads', 'Level bearing surfaces for habitats and equipment'],
      ['Dust-mitigation zones', 'Stabilised crusts protecting seals, optics and suits'],
    ],
  },
  {
    id: 'testing', label: 'Space-environment simulation & testing', tag: 'Vacuum · Thermal · Radiation', tone: 'sky', img: '/assets/rd-testing.webp',
    summary: 'A deep-shaft environmental chamber that reproduces the conditions space hardware must survive, hard vacuum, extreme thermal cycling, altered gravity and radiation, so products can be qualified against representative lunar and orbital conditions in-house.',
    detail: 'Operating our own test facility closes the develop–test–iterate loop before any flight commitment, and lets us qualify both our own systems and partners’ hardware. Separately patent pending.',
    points: [
      ['Hard vacuum', 'Representative lunar and orbital pressure regimes'],
      ['Thermal extremes', 'Hundreds of degrees of diurnal cycling'],
      ['Altered gravity', 'Deep-shaft architecture for reduced-g regimes'],
      ['Radiation', 'Environmental exposure for materials and electronics'],
    ],
  },
  {
    id: 'recycling', label: 'Recycling & circular-process systems', tag: 'Material recovery · Beneficiation', tone: 'plant', img: '/assets/rd-recycling.webp',
    summary: 'Flow-sheets for recovering value from secondary and waste streams: beneficiation of complex feeds, developed from laboratory through pilot scale and into full circular-process design for the resource sector.',
    detail: 'The same microwave, process and simulation tools we apply to ore and regolith carry directly into closing material loops: drying, separation, breakage reduction and recovery of secondary streams.',
    points: [
      ['Material recovery', 'Flow-sheets for secondary & waste streams'],
      ['Beneficiation', 'Upgrading of complex and low-grade feeds'],
      ['Lab → pilot', 'De-risked scale-up before commitment'],
      ['Circular design', 'Closing loops across the process'],
    ],
  },
];
